// Service API pour la base de connaissances (remplace knowledgeBase.ts avec localStorage)
import api from '../config/api';
import { KnowledgeEntry } from './knowledgeBase';
import { publicQuestionsService, PublicQuestion } from './publicQuestionsService';

export interface KnowledgeEntryApi extends KnowledgeEntry {
  id: number;
  created_by?: number;
  created_by_email?: string;
  created_at: string;
  updated_at: string;
  is_active: boolean;
}

class KnowledgeBaseApiService {
  private cache: KnowledgeEntryApi[] | null = null;
  private cacheTimestamp: number = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  // Obtenir toutes les entrées (avec cache)
  async getAllEntries(): Promise<KnowledgeEntry[]> {
    const now = Date.now();
    
    // Utiliser le cache si valide
    if (this.cache && (now - this.cacheTimestamp) < this.CACHE_DURATION) {
      return this.cache.map(this.mapToKnowledgeEntry);
    }

    try {
      const response = await api.get('/chat/knowledge-base/');
      const data = response.data.results || response.data || [];
      this.cache = Array.isArray(data) ? data : [];
      this.cacheTimestamp = now;
      return this.cache.map(this.mapToKnowledgeEntry);
    } catch (error) {
      console.error('Erreur lors du chargement de la base de connaissances:', error);
      // Fallback vers localStorage si l'API échoue
      return this.getFallbackEntries();
    }
  }

  // Rechercher dans la base de connaissances
  // Rechercher dans les questions publiques répondues
  async searchPublicQuestions(query: string): Promise<PublicQuestion | null> {
    try {
      // Récupérer toutes les questions publiques répondues
      let allQuestions: PublicQuestion[] = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const data = await publicQuestionsService.getAllQuestions(page);
        // Filtrer uniquement les questions avec réponses
        const answeredQuestions = data.results.filter(q => q.answer || q.answer_video);
        allQuestions = [...allQuestions, ...answeredQuestions];
        hasMore = !!data.next;
        page++;
        // Limiter à 100 questions pour éviter de surcharger
        if (allQuestions.length >= 100) break;
      }

      if (allQuestions.length === 0) {
        return null;
      }

      // Rechercher la question la plus similaire
      return this.searchInPublicQuestions(allQuestions, query);
    } catch (error) {
      console.error('Erreur lors de la recherche dans les questions publiques:', error);
      return null;
    }
  }

  // Rechercher dans une liste de questions publiques
  private searchInPublicQuestions(questions: PublicQuestion[], query: string): PublicQuestion | null {
    const lowerQuery = query.toLowerCase().trim().replace(/[?.,!;:]/g, '');
    const queryWords = lowerQuery.split(/\s+/).filter(w => w.length > 1);
    
    let bestMatch: { question: PublicQuestion; score: number } | null = null;
    
    for (const question of questions) {
      let score = 0;
      const lowerQuestion = question.question.toLowerCase().trim().replace(/[?.,!;:]/g, '');
      const lowerAnswer = question.answer?.toLowerCase() || '';
      
      // Correspondance exacte de la question
      if (lowerQuestion === lowerQuery) {
        score = 100;
      } else if (lowerQuestion.includes(lowerQuery) || lowerQuery.includes(lowerQuestion)) {
        score = 80;
      }
      
      // Correspondance partielle avec les mots significatifs
      const questionWords = lowerQuestion.split(/\s+/).filter(w => w.length > 2);
      const matchingWords = queryWords.filter(qw => 
        questionWords.some(qw2 => qw2.includes(qw) || qw.includes(qw2))
      ).length;
      
      if (matchingWords >= 3) {
        score += matchingWords * 25;
      } else {
        score += matchingWords * 20;
      }
      
      // Correspondance dans la réponse (score plus faible)
      if (lowerAnswer && lowerAnswer.includes(lowerQuery)) {
        score += 15;
      }
      
      // Bonus pour plusieurs mots correspondants
      if (matchingWords >= queryWords.length * 0.5) {
        score += 10;
      }
      
      if (!bestMatch || score > bestMatch.score) {
        bestMatch = { question, score };
      }
    }
    
    // Retourner seulement si le score est suffisant (au moins 30)
    return bestMatch && bestMatch.score >= 30 ? bestMatch.question : null;
  }

  async search(query: string): Promise<KnowledgeEntry | null> {
    try {
      const entries = await this.getAllEntries();
      console.log(`[KB] Recherche de "${query}" dans ${entries.length} entrées`);
      const result = this.searchInEntries(entries, query);
      if (result) {
        console.log(`[KB] Trouvé: "${result.question}"`);
      } else {
        console.log(`[KB] Aucune correspondance trouvée pour "${query}"`);
      }
      return result;
    } catch (error) {
      console.error('[KB] Erreur lors de la recherche:', error);
      return null;
    }
  }

  // Rechercher dans une liste d'entrées
  private searchInEntries(entries: KnowledgeEntry[], query: string): KnowledgeEntry | null {
    const lowerQuery = query.toLowerCase().trim().replace(/[?.,!;:]/g, ''); // Nettoyer la ponctuation
    const queryWords = lowerQuery.split(/\s+/).filter(w => w.length > 1); // Mots de plus de 1 caractère (au lieu de 2)
    
    // Score de correspondance pour chaque entrée
    let bestMatch: { entry: KnowledgeEntry; score: number } | null = null;
    
    for (const entry of entries) {
      let score = 0;
      const lowerQuestion = entry.question.toLowerCase().trim().replace(/[?.,!;:]/g, '');
      const lowerAnswer = entry.answer.toLowerCase();
      
      // Correspondance exacte de la question (score le plus élevé) - nettoyer aussi la ponctuation
      if (lowerQuestion === lowerQuery) {
        score = 100;
      } else if (lowerQuestion.includes(lowerQuery) || lowerQuery.includes(lowerQuestion)) {
        score = 80;
      }
      
      // Correspondance avec les mots-clés importants (Muhammad, Prophète, etc.)
      const keywordMatches = entry.keywords.filter(kw => {
        const kwLower = kw.toLowerCase();
        return lowerQuery.includes(kwLower) || kwLower.includes(lowerQuery) ||
               queryWords.some(qw => kwLower.includes(qw) || qw.includes(kwLower));
      }).length;
      score += keywordMatches * 30;
      
      // Correspondance partielle avec les mots significatifs de la question
      const questionWords = lowerQuestion.split(/\s+/).filter(w => w.length > 2);
      const matchingWords = queryWords.filter(qw => 
        questionWords.some(qw2 => qw2.includes(qw) || qw.includes(qw2))
      ).length;
      
      // Bonus si plusieurs mots correspondent (pertinence élevée)
      if (matchingWords >= 3) {
        score += matchingWords * 25; // Bonus pour plusieurs mots
      } else {
        score += matchingWords * 20;
      }
      
      // Correspondance dans la réponse (score plus faible) - mais pénaliser si faible
      if (lowerAnswer.includes(lowerQuery) || queryWords.some(qw => lowerAnswer.includes(qw))) {
        score += 10;
      }
      
      // PÉNALISER les correspondances faibles pour éviter les faux positifs
      // Si le score est faible (< 50) et que ce n'est pas une correspondance exacte ou proche, ne pas considérer
      if (score > 0) {
        // Si c'est une correspondance faible (juste quelques mots communs), pénaliser fortement
        if (score < 50 && matchingWords < 3 && keywordMatches === 0) {
          score = score * 0.3; // Réduire le score de 70% pour les faibles correspondances
        }
        
        // Si la question de l'utilisateur est très différente de la question dans la KB, pénaliser
        // Ex: "Pourquoi les musulmans prient-ils" ne doit PAS matcher "Qu'est-ce que le Tawhid ?"
        const queryWordsSet = new Set(queryWords);
        const questionWordsSet = new Set(questionWords);
        const commonWords = queryWords.filter(w => questionWordsSet.has(w));
        const uniqueWords = queryWords.length - commonWords.length;
        
        // Si moins de 50% des mots correspondent, pénaliser
        if (queryWords.length > 0 && (commonWords.length / queryWords.length) < 0.5 && score < 80) {
          score = score * 0.4; // Pénalité forte
        }
        
        if (!bestMatch || score > bestMatch.score) {
          bestMatch = { entry, score };
        }
      }
    }
    
    // Retourner la meilleure correspondance si le score est suffisant (au moins 50 pour éviter les faux positifs)
    // Score minimum plus élevé pour être plus strict
    return bestMatch && bestMatch.score >= 50 ? bestMatch.entry : null;
  }

  // Obtenir les entrées par catégorie
  async getByCategory(category: string): Promise<KnowledgeEntry[]> {
    try {
      const response = await api.get('/chat/knowledge-base/', {
        params: { category }
      });
      const entries = response.data.results || response.data;
      return entries.map(this.mapToKnowledgeEntry);
    } catch (error) {
      console.error('Erreur lors du chargement par catégorie:', error);
      return [];
    }
  }

  // Obtenir toutes les catégories
  async getCategories(): Promise<string[]> {
    try {
      const entries = await this.getAllEntries();
      const categories = new Set<string>();
      entries.forEach(entry => categories.add(entry.category));
      return Array.from(categories);
    } catch (error) {
      console.error('Erreur lors du chargement des catégories:', error);
      return [];
    }
  }

  // Mapper l'entrée API vers KnowledgeEntry
  private mapToKnowledgeEntry(entry: KnowledgeEntryApi): KnowledgeEntry & { is_active?: boolean } {
    return {
      keywords: entry.keywords || [],
      question: entry.question,
      answer: entry.answer,
      category: entry.category,
      arabic: entry.arabic,
      is_active: entry.is_active !== false, // Préserver is_active pour le filtrage
    };
  }

  // Fallback vers localStorage (pour compatibilité)
  private getFallbackEntries(): KnowledgeEntry[] {
    try {
      const saved = localStorage.getItem('knowledge_base_entries');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error('Erreur lors du chargement depuis localStorage:', error);
    }
    return [];
  }

  // Invalider le cache
  invalidateCache(): void {
    this.cache = null;
    this.cacheTimestamp = 0;
  }

  // Obtenir toutes les questions (juste les questions, pas les réponses complètes)
  async getAllQuestions(): Promise<Array<{ id: number; question: string; category: string; keywords: string[] }>> {
    try {
      const response = await api.get('/chat/knowledge-base/all_questions/');
      return response.data.questions || [];
    } catch (error) {
      console.error('Erreur lors du chargement des questions:', error);
      return [];
    }
  }

  // Trouver des questions similaires
  async findSimilarQuestions(
    query: string,
    limit: number = 5,
    excludeId?: number
  ): Promise<Array<{ id: number; question: string; category: string; similarity: number }>> {
    try {
      const params: any = { q: query, limit };
      if (excludeId) {
        params.exclude_id = excludeId;
      }
      const response = await api.get('/chat/knowledge-base/similar_questions/', { params });
      return response.data.questions || [];
    } catch (error) {
      console.error('Erreur lors de la recherche de questions similaires:', error);
      return [];
    }
  }
}

export const knowledgeBaseApiService = new KnowledgeBaseApiService();

